/**
 * The chip expression field — the pure model (no React, no TipTap).
 *
 * The field is a row of chips, ONE PER TOKEN of the expression keyboard's
 * engine (ExpressionKeyboard/engine.js): `price` `×` `units`; `s` `.gpa`;
 * `marks` `[` `i` `]`; a call is a chain `sqrt` `(` `x` `)`. The model is the
 * engine's own `{ toks, cur }` (the caret sits between chips), so:
 *
 *   stored C  --tokenize-->  chips  --toC-->  stored C
 *
 * Nothing is typed as text. A key is turned into chips here ("type it"):
 *   digits             a number chip (more digits grow it; a dot makes it a decimal)
 *   + - * / % ( ) [ ]  an operator or a bracket chip
 *   < > = !            a comparison chip; a second key joins it (< then = is ≤)
 *   , ? : & | ^ ~      their chips
 * Letters and quotes are handled by the field (a suggestion list; a literal
 * being written), with the helpers below.
 */
import {
  tokenize, tokensToC, toC, serialize, pressKey, enclosingCall, keyboardCanEdit, createEnv, OP,
} from '../ExpressionKeyboard/engine';
import { kindOfCType } from '@/utils/expressionKind';

const clone = (toks) => toks.map((t) => ({ ...t }));
/**
 * A value that holds a function: its type is a function pointer
 * (`int (*)(int, int)`), or a type name that is no kind of value the field
 * knows (a typedef'd function pointer such as `BillRule`) and has no fields.
 */
export const holdsFunction = (name, env) => {
  const v = env && env.vars && env.vars.get(name);
  if (!v) return false;
  const t = String(v.type || '').trim();
  if (/\(\s*\*/.test(t)) return true;
  return /^[A-Za-z_]\w*$/.test(t) && kindOfCType(t) === null && !(v.fields && v.fields.length) && !v.elementFields;
};
const VALUE_END = ['var', 'num', 'chr', 'str', ')', ']', 'member'];
const VALUE_START = ['var', 'num', 'chr', 'str', 'fn', '(', 'not', 'addr', 'deref', 'cast', 'sizeof', 'bitnot'];
export const endsValue = (t) => !!t && VALUE_END.includes(t.k);

/** Stored C → chips. */
export const cToTokens = (c, env = createEnv()) => tokenize(String(c || ''), env);
/** Chips → the C to store (the parsed expression; while it does not parse, its tokens). */
export const tokensToStoredC = (toks, env = createEnv()) => toC(toks, env);

const squash = (s) => String(s || '').replace(/\s+/g, '');

/**
 * Can the chip field hold this stored text without changing it? It must be an
 * expression (not a statement — `x = 1`, `i++`), every token a known one, and
 * chips → C must give the same tokens and the same characters back (spaces
 * aside: `n/2` comes back as `n / 2` only once it is edited). A text the field
 * refuses keeps the keyboard window.
 */
export function canChipEdit(c, env = createEnv()) {
  const s = String(c || '');
  if (!s.trim()) return true;
  if (!keyboardCanEdit(s)) return false;
  const toks = tokenize(s, env);
  if (toks.some((t) => t.k === 'bad' || t.open || t.k === 'if')) return false;
  const back = tokensToC(toks);
  if (squash(back) !== squash(s)) return false;
  return JSON.stringify(tokenize(back, env)) === JSON.stringify(toks);
}

// ── typing ──────────────────────────────────────────────────────────────────
// Two-key operators: the chip before the caret and the key typed.
const JOIN = {
  '<=': { k: 'op', v: '<=' }, '>=': { k: 'op', v: '>=' }, '==': { k: 'op', v: '==' },
  '!=': { k: 'op', v: '!=' }, '&&': { k: 'op', v: '&&' }, '||': { k: 'op', v: '||' },
  '<<': { k: 'op', v: '<<' }, '>>': { k: 'op', v: '>>' },
};
const chipC = (t) => {
  if (!t) return '';
  if (t.k === 'op') return t.v;
  if (t.k === 'not') return '!';
  if (t.k === 'addr') return '&';
  return '';
};

/** Insert tokens at the caret, with the comma a call needs between its values. */
function insertAt(model, ts, env) {
  const toks = clone(model.toks);
  let cur = Math.max(0, Math.min(model.cur, toks.length));
  const prev = toks[cur - 1];
  if (ts.length && prev && endsValue(prev) && VALUE_START.includes(ts[0].k)) {
    const call = enclosingCall(toks, cur, env);
    const max = call && (call.variadic || !call.known ? Infinity : call.params.length);
    if (call && call.arg < max - 1) { toks.splice(cur, 0, { k: ',' }); cur += 1; }
  }
  toks.splice(cur, 0, ...ts.map((t) => ({ ...t })));
  return { toks, cur: cur + ts.length };
}

/** Insert a value or a piece of C (`s.gpa`, `marks[i]`, `*p`) at the caret. */
export function insertC(model, c, env = createEnv()) {
  return insertAt(model, tokenize(String(c || ''), env), env);
}

/** Insert a function as its chain `name ( )`, the caret inside the brackets. */
export function insertFunction(model, name, env = createEnv()) {
  return pressKey(model, { t: 'fn', v: name }, env);
}

/** Insert an operator chip by its C spelling (`+`, `*`, `%`, `<=`, `&&`, `!`). */
export function insertOperator(model, op, env = createEnv()) {
  if (op === '!') return insertAt(model, [{ k: 'not' }], env);
  if (op === '(' || op === ')') return insertAt(model, [{ k: op }], env);
  return insertAt(model, [{ k: 'op', v: op }], env);
}

/**
 * One typed key (not a letter, not a quote) → the next model.
 *   → { model, handled }  — handled false: the key means nothing here (refused).
 *   → { model, field: { arrow } } — a dot / arrow after a value: pick a field.
 */
export function typeKey(model, ch, env = createEnv(), { afterFunction = false } = {}) {
  const toks = clone(model.toks);
  const cur = Math.max(0, Math.min(model.cur, toks.length));
  const prev = toks[cur - 1];
  const same = { model: { toks, cur }, handled: true };

  if (ch === ' ') return same;

  // A `(` right after a value that holds a function (a function pointer,
  // `rule(30, 4)`): it is called, as a function chip is.
  if (ch === '(' && prev && prev.k === 'var' && holdsFunction(prev.v, env)) {
    toks[cur - 1] = { k: 'fn', v: prev.v };
    toks.splice(cur, 0, { k: '(' }, { k: ')' });
    return { model: { toks, cur: cur + 1 }, handled: true };
  }
  // A function chip already carries its brackets: the `(` typed right after
  // it is the one it has, so typing goes on into its value (`pow(a, 2)`).
  if (ch === '(' && afterFunction && prev && prev.k === '(') return same;
  // A `)` typed where the call's own `)` already is moves past it — when every
  // bracket is closed already (an extra one would be left open otherwise).
  if (ch === ')' && toks[cur] && toks[cur].k === ')') {
    const opens = toks.filter((t) => t.k === '(').length;
    const closes = toks.filter((t) => t.k === ')').length;
    if (opens <= closes) return { model: { toks, cur: cur + 1 }, handled: true };
  }
  // A sign right after an exponent belongs to the number: 1e-5.
  if ((ch === '+' || ch === '-') && prev && prev.k === 'num' && /^\d[\d.]*[eE]$/.test(prev.v)) { prev.v += ch; return same; }

  // Two-key operators join the chip before the caret.
  const joined = JOIN[chipC(prev) + ch];
  if (prev && joined && ['op', 'not', 'addr'].includes(prev.k)) {
    toks[cur - 1] = { ...joined };
    return { model: { toks, cur }, handled: true };
  }
  // `-` then `>` after a value: the arrow to a field.
  if (ch === '>' && prev && prev.k === 'op' && prev.v === '-' && endsValue(toks[cur - 2])) {
    toks.splice(cur - 1, 1);
    return { model: { toks, cur: cur - 1 }, handled: true, field: { arrow: true } };
  }

  if (/\d/.test(ch)) {
    if (prev && prev.k === 'num' && (!/[xX]/.test(prev.v) || NUMBER_PREFIX.test(prev.v + ch))) { prev.v += ch; return same; }
    return { model: insertAt({ toks, cur }, [{ k: 'num', v: ch }], env), handled: true };
  }
  if (ch === '.') {
    if (prev && prev.k === 'num' && !/[.eExX]/.test(prev.v)) { prev.v += '.'; return same; }
    if (endsValue(prev)) return { model: { toks, cur }, handled: true, field: { arrow: false } };
    return { model: insertAt({ toks, cur }, [{ k: 'num', v: '0.' }], env), handled: true };
  }
  if ('+-/%^|<>'.includes(ch)) return { model: insertAt({ toks, cur }, [{ k: 'op', v: ch }], env), handled: true };
  if (ch === '*') return { model: insertAt({ toks, cur }, [endsValue(prev) ? { k: 'op', v: '*' } : { k: 'deref' }], env), handled: true };
  if (ch === '&') return { model: insertAt({ toks, cur }, [endsValue(prev) ? { k: 'op', v: '&' } : { k: 'addr' }], env), handled: true };
  if (ch === '=') return { model: insertAt({ toks, cur }, [{ k: 'op', v: '==' }], env), handled: true };
  if (ch === '!') return { model: insertAt({ toks, cur }, [{ k: 'not' }], env), handled: true };
  if (ch === '~') return { model: insertAt({ toks, cur }, [{ k: 'bitnot' }], env), handled: true };
  if ('()[],?:'.includes(ch)) return { model: insertAt({ toks, cur }, [{ k: ch }], env), handled: true };
  // The maths keys typed as themselves.
  const MATH = { '×': '*', '÷': '/', '−': '-', '≤': '<=', '≥': '>=', '≠': '!=' };
  if (MATH[ch]) return { model: insertAt({ toks, cur }, [{ k: 'op', v: MATH[ch] }], env), handled: true };
  return { model: { toks, cur }, handled: false };
}

/** Backspace: a number loses its last digit, anything else goes. */
export function backspace(model, env = createEnv()) {
  return pressKey(model, { t: 'back' }, env);
}

/** Delete the chips from `from` to `to` (a selection). */
export function deleteRange(model, from, to) {
  const toks = clone(model.toks);
  const a = Math.max(0, Math.min(from, to));
  const b = Math.min(toks.length, Math.max(from, to));
  toks.splice(a, b - a);
  return { toks, cur: a };
}

// ── literals being written ─────────────────────────────────────────────────
/** A quote starts a text ("…") or character ('…') literal chip, open. */
export function startLiteral(model, quote, env = createEnv()) {
  const k = quote === "'" ? 'chr' : 'str';
  const m = insertAt(model, [{ k, v: '', open: true }], env);
  return { model: m, index: m.cur - 1 };
}

/**
 * A key typed into the open literal at `index`. The closing quote, or Enter,
 * closes it. A character closes by itself after one character (two after a
 * backslash: '\n').
 *   → { model, closed }
 */
export function literalKey(model, index, ch) {
  const toks = clone(model.toks);
  const t = toks[index];
  if (!t || !t.open) return { model, closed: true };
  const quote = t.k === 'chr' ? "'" : '"';
  const escaped = /(^|[^\\])(\\\\)*\\$/.test(t.v);
  if (ch === quote && !escaped) {
    toks[index] = { k: t.k, v: t.v };
    return { model: { toks, cur: index + 1 }, closed: true };
  }
  const v = t.v + ch;
  if (t.k === 'chr' && (/^[^\\]$/.test(v) || /^\\.$/.test(v))) {
    toks[index] = { k: 'chr', v };
    return { model: { toks, cur: index + 1 }, closed: true };
  }
  toks[index] = { ...t, v };
  return { model: { toks, cur: index + 1 }, closed: false };
}

/** Close the open literal at `index` as it is (Enter / leaving the field). */
export function closeLiteral(model, index) {
  const toks = clone(model.toks);
  const t = toks[index];
  if (!t || !t.open) return model;
  if (t.k === 'chr' && !t.v) { toks.splice(index, 1); return { toks, cur: index }; }
  toks[index] = { k: t.k, v: t.v };
  return { toks, cur: index + 1 };
}

/** Backspace inside the open literal: its last character, or the chip when empty. */
export function literalBack(model, index) {
  const toks = clone(model.toks);
  const t = toks[index];
  if (!t) return { model, closed: true };
  if (!t.v) { toks.splice(index, 1); return { model: { toks, cur: index }, closed: true }; }
  toks[index] = { ...t, v: t.v.slice(0, -1) };
  return { model: { toks, cur: index + 1 }, closed: false };
}

// ── what a chip shows ────────────────────────────────────────────────────────
const CHAR_WORDS = { '\\n': 'new line', '\\t': 'tab', ' ': 'space', '\\0': 'end of text', "\\'": 'quote', '\\\\': 'backslash' };
const CAST_WORDS = {
  int: 'as a whole number', double: 'as a decimal', float: 'as a decimal', char: 'as a character',
  long: 'as a whole number', 'unsigned char': 'as a character code', 'unsigned int': 'as a whole number',
};

/**
 * The text on one chip. Operators are always their maths symbol (× ÷ mod = ≠
 * ≤ ≥ and or not), as the keyboard's field shows them; in words-only mode
 * ('ps') nothing on a chip is C.
 */
export function chipLabel(t, courseMode = 'c') {
  const ps = courseMode === 'ps';
  switch (t.k) {
    case 'var': case 'num': case 'fn': case 'type': case 'bad': return t.v;
    case 'chr': return t.open ? `'${t.v}` : (ps && CHAR_WORDS[t.v] ? CHAR_WORDS[t.v] : `'${t.v}'`);
    case 'str': return t.open ? `"${t.v}` : `"${t.v}"`;
    case 'op': return (OP[t.v] && OP[t.v].math) || t.v;
    case 'member': return ps || !t.arrow ? `.${t.v}` : `->${t.v}`;
    case 'cast': return ps ? (CAST_WORDS[t.v] || `as ${t.v}`) : `(${t.v})`;
    case 'not': return 'not';
    case 'bitnot': return '~';
    case 'addr': return ps ? 'address of' : '&';
    case 'deref': return ps ? 'value at' : '*';
    case 'sizeof': return ps ? 'size of' : 'sizeof';
    case 'if': return 'if';
    case '?': return 'then';
    case ':': return 'otherwise';
    default: return t.k;
  }
}

/** What sort of chip it is, for its look. */
export function chipKind(t) {
  if (['var', 'member'].includes(t.k)) return 'value';
  if (['num', 'chr', 'str'].includes(t.k)) return 'literal';
  if (t.k === 'fn') return 'function';
  if (['(', ')', '[', ']', ','].includes(t.k)) return 'bracket';
  return 'operator';
}

/** Chips that sit tight against the one before (`s.gpa`, `marks[i]`, `sqrt(x)`). */
export function tightBefore(toks, j) {
  const t = toks[j];
  const prev = toks[j - 1];
  if (!prev) return true;
  if (['member', ']', ')', ','].includes(t.k)) return true;
  // `&count` / `*p` read as one thing: the name sits right against its & or *.
  if (prev.k === 'addr' || prev.k === 'deref') return true;
  if (t.k === '[') return true;
  if (t.k === '(' && (prev.k === 'fn' || prev.k === 'sizeof' || prev.k === 'member')) return true;
  return ['(', '['].includes(prev.k);
}

/** The maths form of the chips, one line (`price × units`). */
export const mathText = (toks) => serialize(toks).text;

// ── numbers typed with their C spelling ────────────────────────────────────
// What a number chip can grow into by typing: 0x1F, 12U, 100L, 1103515245UL,
// 1.5f, 2.5e-3. `numberCanType` says whether a whole number can be typed so.
const NUMBER_DONE = /^(?:0[xX][0-9a-fA-F]+(?:[uU](?:l|L|ll|LL)?|(?:l|L|ll|LL)[uU]?)?|\d+(?:(?:[uU](?:l|L|ll|LL)?|(?:l|L|ll|LL)[uU]?)|(?:\.\d*)?(?:[eE][+-]?\d+)?)|\d+(?:\.\d*)?(?:[eE][+-]?\d+)?[fFlL]|\d*\.\d+(?:[eE][+-]?\d+)?[fFlL]?)$/;
const NUMBER_PREFIX = /^(?:0[xX][0-9a-fA-F]*(?:[uU](?:l|L|ll|LL)?|(?:l|L|ll|LL)[uU]?)?|\d+(?:[uU](?:l|L|ll|LL)?|(?:l|L|ll|LL)[uU]?)?|\d+(?:\.\d*)?(?:[eE][+-]?\d*)?[fFlL]?)$/;
export const numberCanType = (v) => NUMBER_DONE.test(String(v || '')) && !/^\d+[fF]$/.test(String(v));

/**
 * A letter typed right after a number chip, when it carries on the number in
 * C (x after 0, hex digits, the U / L / f suffixes, an e exponent) → the next
 * model; otherwise null (the letter starts a name, as usual).
 */
export function numberKey(model, ch) {
  const cur = Math.max(0, Math.min(model.cur, model.toks.length));
  const prev = model.toks[cur - 1];
  if (!prev || prev.k !== 'num' || !/^[A-Za-z]$/.test(ch)) return null;
  const v = prev.v + ch;
  if (!NUMBER_PREFIX.test(v)) return null;
  // `f` only ends a decimal (1.5f): 1f is not C.
  if (/[fF]$/.test(v) && !/^0[xX]/.test(v) && !/[.eE]/.test(prev.v)) return null;
  const toks = clone(model.toks);
  toks[cur - 1] = { ...prev, v };
  return { toks, cur };
}

// ── casts and sizeof typed ───────────────────────────────────────────────────
/** A typed word as a C type the engine knows (`int`, `unsigned char`, `struct Mark`, `int *`), tidied; else null. */
export function typedTypeWord(word, env = createEnv()) {
  const w = String(word || '').trim();
  if (!w || !/^[A-Za-z_][\w\s*]*$/.test(w)) return null;
  const toks = tokenize(`(${w})x`, env);
  return toks.length === 2 && toks[0].k === 'cast' ? toks[0].v : null;
}

/** Words that a type goes on after (`unsigned` char, `struct` Mark). */
export const TYPE_LEAD = /^(?:(?:unsigned|signed|const|long|short|struct|union|enum)\s+)*(?:unsigned|signed|const|long|short|struct|union|enum)$/;

/**
 * A type typed right after a `(`: after `sizeof (` it is the type to measure
 * (`sizeof(struct Mark)`); after a plain `(` it turns that bracket into a cast
 * chip (`(int)`). → the next model, or null when it is not a type there.
 */
export function typeAfterBracket(model, word, env = createEnv()) {
  const ty = typedTypeWord(word, env);
  if (!ty) return null;
  const toks = clone(model.toks);
  const cur = Math.max(0, Math.min(model.cur, toks.length));
  const prev = toks[cur - 1];
  if (!prev || prev.k !== '(') return null;
  const before = toks[cur - 2];
  if (before && before.k === 'sizeof') {
    toks.splice(cur, 0, { k: 'type', v: ty });
    const at = toks[cur + 1] && toks[cur + 1].k === ')' ? cur + 2 : cur + 1;
    return { toks, cur: at };
  }
  if (before && before.k === 'fn') return null; // a call's own bracket: never a cast
  toks[cur - 1] = { k: 'cast', v: ty };
  return { toks, cur };
}

/** A cast chip at the caret: `(int)`, `(double)`. */
export function insertCast(model, type, env = createEnv()) {
  return pressKey(model, { t: 'cast', v: type }, env);
}

/** "size of" at the caret: the chain `sizeof ( )`, the caret inside. */
export function insertSizeof(model, env = createEnv()) {
  return pressKey(model, { t: 'sizeof' }, env);
}
